import { connect } from 'pwa-helpers';
import { css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit-html/directives/class-map.js';
import searchIcon from '../../common/icons/search-icon';
import { selectSelectedCatalog, selectSortBy } from '../search/store/search.selector';
import { setSelectedCatalog } from '../search/store/search.store';
import { performSearchSpecificIsbn } from '../search/thunks/search.thunk';
import '../product-card/product-card-display';
import '../search/content-card-display';
import { BaseComponent } from '../base-component/base-component';
import store from '../../store/store';
import { booleanConverter } from '../../common/converters';
import { smallSpinner } from '../../common/constants/api-constants';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { getUniqueId, mapCatalog } from '../../common/utils/util';
import { isHeCustomStoreCart, isHePrivateStoreCart } from '../cart/shop-cart/store/shop-cart.selectors';
import { Catalog } from '../../common/constants/catalog';
import { StoreState } from '../../store/store.interface';

import { getUserDataForUserAnalytics } from '../user-profile/user-profile.selectors';
import emit from '../../common/events';
import environment from '../../common/environment';
import { getSearchRequestFromUrl, performV2Search } from './thunks/perform-search-v2.thunk';
import { isLoaded, selectSuggestions } from './store/suggestion-v2.selectors';
import { getSuggestions } from './thunks/suggestion-v2.thunk';
import { SuggestionsV2 } from './interface/suggestion-v2.interface';
import { TopCategoryFacetValue } from '../search/interfaces/suggestion.interface';
import { clearSuggestions } from './store/suggestion-v2.store';
import {
  isFailure,
  isLoading,
  isSuccess,
  selectFacets,
  selectTotal,
  selectV2Request,
} from './store/search-v2.selectors';
import { SearchV2Facet } from './interface/search-v2.interface';
import debounce from 'lodash/debounce';
import { DEFAULT_SEARCH_TERM_REDIRECTS, SearchTermRedirects } from '../search/search';
import { productDiscoveryQPKey, productDiscoveryQPValue } from '../../common/constants/anayltics.constants';
import { appendQueryParamToUrl } from '../../common/utils/analytics-util';

declare global {
  interface Window {
    location: Location;
  }
}

const catalogs = ['he', 'seg', 'content'];

export enum Size {
  SMALL = 'small',
  DEFAULT = 'default',
  LARGE = 'large',
}

/**
 * An element to search for products
 *
 * @event click - A user has clicked something within the search component.
 *        <br><br>&cross; dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: n/a
 * @event searched - A searched with their own search term.
 *        <br><br><span style="color: green">&check;</span> dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: { term }
 * @event searched-for-category-suggestion - A user clicked on a category suggestion.
 *        <br><br><span style="color: green">&check;</span> dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: { term }
 * @event searched-for-term-suggestion - A user clicked on a term suggestion.
 *        <br><br><span style="color: green">&check;</span> dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: { term }
 */
@customElement('mhecomm-search-v2')
export class SearchV2 extends connect(store)(BaseComponent) {
  static styles = [
    css`
      .dropdown-menu {
        z-index: 1050 !important;
      }
    `,
  ];
  /**
   * The text to display when there is at least one item in the cart.
   */
  @property()
  placeholder = 'Search by ISBN, Title, Author, Keyword';

  /**
   * The search query in the input field
   */
  @property()
  searchQuery = '';

  /**
   * set isLoading on search click to show loader on icon
   */
  @state()
  private isLoading = false;

  /**
   * The search query in the input field
   */
  @property({ converter: booleanConverter })
  autoLoad = false;

  /**
   * The number of results to load. Only used when loadResults is true.
   */
  @property({ type: Number })
  pageSize = 10;

  /**
   * The number of results to load. Only used when loadResults is true.
   */
  @property()
  size = Size.DEFAULT;

  /**
   * Render the search component in light mode.
   */
  @property({ converter: booleanConverter })
  lightMode = false;

  /**
   * The catalog to search within. One of the following values: unified, he, seg, seg-store, and content. seg-store cannot be used in conjunction with useSuggestions="true".
   */
  @property()
  catalog = 'unified';

  /**
   * The store to search within.
   */
  @property()
  store: string | undefined;

  /**
   * The filter to search within.
   */
  @property()
  businessUnit: string | undefined;

  /**
   * The filter to search within.
   */
  @property()
  product: string | undefined;

  /**
   * The filter to search within.
   */
  @property()
  persona: string | undefined;

  /**
   * The filter to search within.
   */
  @property()
  createdByDivision: string | undefined;

  /**
   * The filter to search within.
   */
  @property()
  searchResultPage = 'default';

  /**
   * Whether the suggestions should be open or not
   */
  @property({ converter: booleanConverter })
  suggestionsOpen = false;

  /**
   * Whether or not the suggestion dropdown should be active
   */
  @property({ converter: booleanConverter })
  useSuggestions = true;

  /**
   * Whether or not the search results should be loaded. If this is true, the user will not be navigated to the search results page after performing a search. If a search results component is on the page, that component will load in the search results.
   */
  @property({ converter: booleanConverter })
  loadResults = false;

  /**
   * A json object configuring search term redirects. If the user searches on one of the provided search terms
   * the user will be redirected to the configured page instead of doing a normal search.
   */
  @property({ type: Object })
  searchTermRedirects: SearchTermRedirects = DEFAULT_SEARCH_TERM_REDIRECTS;

  /**
   * is plp search within or not
   */
  @property({ converter: booleanConverter })
  plpSearch = false;

  /**
   * Parameter to determine the page the search component is getting called, default is empty string
   */
  @property({ type: String })
  searchPageType = '';

  // Defaults to true, and then the stateChanged will update this as needed
  @state()
  private hide = true;

  @state()
  private suggestions: SuggestionsV2 | undefined;

  /**
   * focus index of the catalog menu item focused using arrow keys
   */
  @state()
  private currentFocus = -1;

  /**
   * get search selected catalog
   */
  @state()
  private selectedCatalog;

  /**
   * set isSearchSubmit on button click to show suggestions or not
   */
  @state()
  private isSearchSubmit = false;

  @state()
  private isApiStatusSuccess = false;

  @state()
  private isFieldValueFilterAvailable = false;

  @state()
  private supportResultsAnalyticsPending = false;

  @state()
  private lastSupportResultsAnalyticsSignature = '';

  /**
   * Tracks if a selection has been made from the suggestion dropdown
   */
  @state()
  private hasSelectionBeenMade = false;

  @state()
  private searchTriggeredOnLoad = false;

  /**
   * Text to describe mhecomm-search-v2
   */
  @property()
  ariaDescriptionText =
    'When autocomplete catalog suggestions are available use up and down arrows to review and enter to select.';

  _id = getUniqueId();

  private getRequestOwnerId(): string {
    // Always return the instance-unique id so ownership is not shared between duplicated
    // component instances (e.g. header + drawer) that happen to expose the same `id`.
    return this._id;
  }

  connectedCallback() {
    super.connectedCallback();

    this._addCloseSuggestionsListeners();

    if (this.autoLoad && this.loadResults) {
      this._search(true);
    }
  }
  constructor() {
    super();
    this.addEventListener('input', debounce(this._inputUpdate, 500));
    this.addEventListener('input', this._updateSearchQuery);
  }

  renderComp() {
    return html`
      <div class="w-100 input-group ${this._getInputGroupClasses()}" tabindex="-1" role="region" aria-label="Search">
        <input
          aria-label="search"
          id="input-search-box"
          data-automation-id="input-search-box"
          class="form-control ms-0 ${this._getInputClasses()}"
          type="text"
          placeholder=${this.placeholder}
          .value=${this.searchQuery}
          @focus=${this._focus}
          @keypress=${this._keyPress}
          @keydown=${this._catalogMenuKeyboardNav}
          autocomplete="off"
          aria-autocomplete="list"
          aria-owns=${ifDefined(this.useSuggestions ? `${this.id}-autocomplete-catalog-menu` : undefined)}
          aria-activedescendant=${ifDefined(
            this.useSuggestions && this.currentFocus !== -1 ? `active-option-${this.currentFocus}` : undefined
          )}
          aria-controls="${this.id}-autocomplete-catalog-menu"
          role=${ifDefined(this.useSuggestions ? 'combobox' : undefined)}
          aria-expanded=${ifDefined(this.useSuggestions ? this.suggestionsOpen : undefined)} />
        <button
          id="submit-search-button"
          data-automation-id="submit-search-button"
          class="btn ${this._getButtonClass()}"
          aria-label="Search Submit"
          @click=${() => this._search(false, true)}>
          ${this.isLoading ? smallSpinner() : searchIcon}
        </button>
      </div>
      <div class="dropdown">
        <ul
          class="dropdown-menu position-relative position-xl-absolute w-100 ${this._suggestionClasses()}"
          id="${this.id}-autocomplete-catalog-menu"
          role="listbox"
          aria-label="Search results">
          ${this.suggestions?.topCategoryFacetValues?.map(
            (suggestion, index) => html`
              <li
                id="active-option-${index}"
                role="option"
                tabindex="0"
                aria-selected=${this.currentFocus === index}
                aria-describedby="suggestion-${index}"
                @click=${(e: Event) => this._searchForCategorySuggestion(e, suggestion)}
                @keydown=${(e: KeyboardEvent) =>
                  this._handleOptionKeyDown(e, () => this._searchForCategorySuggestion(e, suggestion))}
                @mouseenter=${() => this._updateFocusedItem(index)}
                @focus=${() => this._updateFocusedItem(index)}>
                <span class="dropdown-item text-wrap">
                  ${this.firstSuggestion()} in
                  <span class="text-primary">${suggestion.label}</span>
                  (${suggestion.count})
                </span>
              </li>
            `
          )}
          ${this.hasSuggestions() && this.hasFacetValueSuggestions()
            ? html` <li aria-hidden="true"><hr class="dropdown-divider" /></li> `
            : ''}
          ${this.suggestions?.suggestions?.map(
            (suggestion, index) => html`
              <li
                id="active-option-${this._categoryCount() + index}"
                role="option"
                tabindex="0"
                aria-selected=${this.currentFocus === this._categoryCount() + index}
                aria-describedby="suggestion-${this._categoryCount() + index}"
                @click=${(e) => this._searchForTermSuggestion(e, suggestion)}
                @keydown=${(e: KeyboardEvent) =>
                  this._handleOptionKeyDown(e, () => this._searchForTermSuggestion(e, suggestion))}
                @mouseenter=${() => this._updateFocusedItem(this._categoryCount() + index)}
                @focus=${() => this._updateFocusedItem(this._categoryCount() + index)}>
                <span class="dropdown-item text-wrap">${suggestion}</span>
              </li>
            `
          )}
        </ul>
      </div>
    `;
  }

  private _getInputClasses() {
    return classMap({
      'border-0': this.lightMode,
      'search-input-light': this.lightMode,
    });
  }

  private _getInputGroupClasses() {
    return classMap({
      'd-none': this.hide,
      'input-group-sm': this.size === Size.SMALL,
      'input-group-lg': this.size === Size.LARGE,
    });
  }

  private _getButtonClass() {
    return classMap({
      'btn-primary': !this.lightMode,
      'btn-light': this.lightMode,
    });
  }

  /**
   * Adds 'focusout' and 'click' event listeners to the body, as 'focusin' doesn't work properly on Safari, causing result links to not work
   */
  private _addCloseSuggestionsListeners() {
    ['focusout', 'click'].forEach((event) =>
      document.body.addEventListener(event, (evt: any) => this._closeSuggestions(evt))
    );
  }

  private _closeSuggestions(event: any) {
    if (!event.composedPath().includes(this)) {
      this.suggestionsOpen = false;
      this.currentFocus = -1; // Reset focus when closing suggestions
    }
  }

  private _suggestionClasses() {
    return classMap({
      show: this.suggestionsOpen && (this.hasSuggestions() || this.hasFacetValueSuggestions()),
    });
  }

  private _setSearchPlaceholder() {
    if (this.selectedCatalog) {
      this.placeholder = {
        [Catalog.unified]: 'Search by ISBN, Title, Author, Keyword',
        [Catalog.seg]: 'Search by ISBN, MHID, Title, State or Subject',
        [Catalog.he]: 'Search by ISBN, Title, Author, Keyword',
        [Catalog.content]: 'Search for Topics & Ideas',
      }[this.selectedCatalog];
    }
  }

  stateChanged(state: StoreState) {
    const request = selectV2Request(state);
    const requestOwnerId = request?.ownerId;
    const isOwnedSupportRequest = requestOwnerId
      ? requestOwnerId === this.getRequestOwnerId()
      : this.supportResultsAnalyticsPending;

    const hasOwnedSupportRequestFailed =
      this.supportResultsAnalyticsPending && isOwnedSupportRequest && !isLoading(state) && !!isFailure(state);
    if (hasOwnedSupportRequestFailed) {
      this.supportResultsAnalyticsPending = false;
    }

    if (isSuccess(state) !== this.isApiStatusSuccess) {
      this.isApiStatusSuccess = isSuccess(state);
      if (this.isApiStatusSuccess) {
        const totalResults = selectTotal(state);
        const filters = this.isFieldValueFilterAvailable ? {} : this.filterValues();
        const analyticsSignature = JSON.stringify({
          id: this.id,
          term: this.searchQuery,
          store: this.store,
          totalResults,
          searchPageType: this.searchPageType,
          fieldValueFilter: filters,
        });

        // Emit only when this instance is the owner of the pending request. The previous
        // search-term based fallback allowed one component (e.g. legacy support search) to
        // emit for a request owned by another instance (e.g. Search V3), causing duplicate
        // `search-results-v2` events for the same search. The signature guard is scoped
        // per request (reset at each dispatch point) and only protects against a double
        // `stateChanged` tick emitting twice within the same success cycle.
        if (
          this.supportResultsAnalyticsPending &&
          isOwnedSupportRequest &&
          this.lastSupportResultsAnalyticsSignature !== analyticsSignature
        ) {
          this.emitSearch(state, totalResults, filters);
          this.lastSupportResultsAnalyticsSignature = analyticsSignature;
        }

        this.supportResultsAnalyticsPending = false;
      }
    }

    if (this.loadResults) {
      this.selectedCatalog = selectSelectedCatalog(state);
      if (this.selectedCatalog) {
        this.catalog = this.selectedCatalog;
      }
    }

    this.suggestions = selectSuggestions(state, this._id);

    if (
      (this === document.activeElement || this._hasElementFocus()) &&
      isLoaded(state, this._id) &&
      !this.hasSelectionBeenMade
    ) {
      this.suggestionsOpen = true;
    }

    if (this.isSearchSubmit || this.hasSelectionBeenMade) {
      this.suggestionsOpen = false;
      this.hasSelectionBeenMade = false; // Reset for next search
    }

    this.hide = isHeCustomStoreCart(state) || !!isHePrivateStoreCart(state);
    this._setSearchPlaceholder();
  }

  /**
   * Checks if the search component or any of its child elements has focus
   * This helps support both direct usage and usage within tabbed-search component
   */
  private _hasElementFocus(): boolean {
    const activeElement = document.activeElement;

    // Check if the active element is inside the shadow DOM
    if (this.shadowRoot?.activeElement) {
      return true;
    }

    // Check if the active element is the input element within this component
    if (activeElement && this.inputElement() === activeElement) {
      return true;
    }

    // Check if the active element is inside this component
    return (
      activeElement !== null &&
      (this.contains(activeElement) ||
        this.shadowRoot?.contains(activeElement) ||
        activeElement.closest(this.tagName.toLowerCase()) === this)
    );
  }

  emitSearch(state: StoreState, totalCount: number, filters: any) {
    emit({
      element: this,
      name: 'search-results-v2',
      detail: {
        id: this.id,
        term: this.searchQuery,
        redirect: false,
        isSearchRedirect: false,
        store: this.store,
        url: window.location.href,
        user: getUserDataForUserAnalytics(state),
        totalResults: totalCount,
        fieldValueFilter: filters,
        catalog: 'SUPPORT',
        searchPageType: this.searchPageType,
        searchText: {
          productDiscovery: 'search',
          searchAttempts: 1,
          searchTerms: [{
            searchTerm: this.searchQuery,
            totalSearchResults: totalCount,
          }],
          pageDetails: {
            pageNumber: 1,
            index: 1,
            currentPageLength: 10,
          },
        },
      },
    });
  }

  /**
   * The most releveant suggestion.
   */
  firstSuggestion() {
    return this.suggestions?.suggestions && this.suggestions?.suggestions.length > 0
      ? this.suggestions?.suggestions[0]
      : this.searchQuery;
  }

  /**
   * Whether or not there are term suggestions based on the provided search term.
   */
  hasSuggestions(): boolean {
    return (
      this.suggestions !== undefined &&
      this.suggestions.suggestions !== undefined &&
      this.suggestions.suggestions.length >= 1
    );
  }

  /**
   * Whether or note there are facet value suggestions based on the provided search term.
   */
  hasFacetValueSuggestions(): boolean {
    return (
      this.suggestions !== undefined &&
      this.suggestions.topCategoryFacetValues !== undefined &&
      this.suggestions.topCategoryFacetValues?.length >= 1
    );
  }

  private _categorySuggestionUrl(suggestion: TopCategoryFacetValue) {
    const mapping: any = {
      'Prek-12': 'seg',
      HigherEd: 'he',
    };
    const mappedBU = mapping[suggestion.name] || suggestion.name;

    if (catalogs.includes(this.catalog) && !catalogs.includes(mappedBU)) {
      return this._searchUrl({
        searchQuery: this.firstSuggestion(),
        businessUnit: this.catalog,
        discipline: suggestion.name,
      });
    } else {
      return this._searchUrl({ searchQuery: this.firstSuggestion(), businessUnit: mappedBU });
    }
  }

  private _termSuggestionUrl(suggestion: string) {
    if (catalogs.includes(this.catalog)) {
      return this._searchUrl({ searchQuery: suggestion, businessUnit: this.catalog });
    } else {
      return this._searchUrl({ searchQuery: suggestion });
    }
  }

  private _keyPress(event: KeyboardEvent) {
    this.isSearchSubmit = false;
    if ((event.which === 13 || event.keyCode === 13 || event.key === 'Enter') && this.currentFocus == -1) {
      this._search(false);
      this.suggestionsOpen = false;
      this.hasSelectionBeenMade = true;
      if (event.key === 'Enter') {
        this.inputElement().blur();
      }
    }
  }

  private _updateSearchQuery() {
    this.searchQuery = this.inputElement().value;
  }
  private _inputUpdate() {
    if (this.searchQuery.length >= 3 && this.useSuggestions) {
      this.currentFocus = -1; // Reset focus when updating input
      this._updateSuggestions();
      const catalogMenuListItems = this.catalogMenuListItems();
      this.unFocusListElement(catalogMenuListItems);
    } else {
      this.suggestionsOpen = false;
      this.currentFocus = -1; // Reset focus when closing suggestions
    }
  }

  private _focus() {
    if (this.searchQuery.length >= 3 && this.useSuggestions) {
      this.suggestionsOpen = true;
      this.currentFocus = -1; // Reset focus when opening suggestions
      const catalogMenuListItems = this.catalogMenuListItems();
      this.unFocusListElement(catalogMenuListItems);
    }
  }

  private _updateSuggestions() {
    store.dispatch(getSuggestions(this._id, this.searchQuery));
  }

  private async _search(isPageLoad: boolean, isSearchSubmit = false) {
    if (isPageLoad) {
      const searchRequest = getSearchRequestFromUrl(this.pageSize) || {};
      this.searchQuery = searchRequest.searchQuery;
      this.searchTriggeredOnLoad = true;
      return this.loadSearchResults(searchRequest);
    } else {
      this.isSearchSubmit = isSearchSubmit;
      this.currentFocus = -1; // Reset focus when searching
      const redirect = this.checkSearchRedirects();
      const state = store.getState() as StoreState;
      this.searchTriggeredOnLoad = false;

      emit({
        element: this,
        name: 'search',
        detail: {
          id: this.id,
          term: this.searchQuery,
          redirect: redirect,
          isSearchRedirect: !!redirect,
          store: this.store,
          catalog: 'SUPPORT',
          url: window.location.href,
          searchPageType: this.searchPageType,
          user: getUserDataForUserAnalytics(state),
        },
      });

      if (redirect) {
        window.location.href = appendQueryParamToUrl(redirect, productDiscoveryQPKey, productDiscoveryQPValue);
      } else if (!this.loadResults) {
        this.isLoading = true;
        const { urlToGo } = (await this.getIsbnToRedirect(
          mapCatalog(this.catalog),
          this.searchQuery,
          this.plpSearch
        )) ?? { urlToGo: '' };
        if (urlToGo) {
          this.goToPage(urlToGo);
          return;
        }
        this.goToSearchPage();
      } else if (this.loadResults) {
        this.loadSearchResults({});
      } else {
        this.goToSearchPage();
      }
    }
  }

  private async getIsbnToRedirect(catalog, isbn, isPlpSearch) {
    if (isPlpSearch) {
      catalog = Catalog.seg_product;
    }

    return performSearchSpecificIsbn({ catalog, searchTerms: isbn });
  }

  private goToSearchPage() {
    this.goToPage(this._searchUrl({ searchQuery: this.searchQuery, businessUnit: this.catalog }));
  }

  private goToPage(url: string) {
    window.location.href = url;
  }

  /**
   * Sends a 'search' event to the data layer with relevant data.
   *
   * @param supressEmit If true, no data layer event will be logged
   */
  private checkSearchRedirects(catalog?: string) {
    if (!this.searchTermRedirects?.redirects) {
      return;
    }

    const state = store.getState() as StoreState;
    this.selectedCatalog = selectSelectedCatalog(state);
    const redirect = this.searchTermRedirects.redirects.find((redirect) => {
      return (
        this.searchQuery.toLowerCase() === redirect.searchTerm.toLowerCase() &&
        [catalog, mapCatalog(this.catalog), this.selectedCatalog, 'ALL'].includes(redirect.catalog)
      );
    });

    return redirect?.target;
  }

  loadSearchResults({
    searchQuery = '',
    page = 1,
    sortby = '',
    catalog = '',
    fieldValueFilter = {},
    isCatalogSet = false,
  }) {
    this.isFieldValueFilterAvailable = Object.keys(fieldValueFilter).length > 0;
    // Reset per-request analytics guards before dispatching a new search so repeat
    // searches with the same payload still emit `search-results-v2`.
    this.supportResultsAnalyticsPending = true;
    this.lastSupportResultsAnalyticsSignature = '';
    const state = store.getState() as StoreState;
    const currentSort = selectSortBy(state);
    const searchCatalog = mapCatalog(catalog || this.catalog);

    store
      .dispatch(
        performV2Search({
          ownerId: this.getRequestOwnerId(),
          searchTerms: searchQuery || this.searchQuery,
          pageLength: this.pageSize,
          start: page,
          facets: true,
          fieldValueFilter: this.isFieldValueFilterAvailable ? fieldValueFilter : this.filterValues(),
        })
      )
      .then(() => this._focusSearchResultsRegion());
  }

  /**
   * Sets focus to the search results info bar for screen reader announcement.
   * Only applies when the aria-live region is not being used on the pagination message component.
   */
  private _focusSearchResultsRegion(): void {
    const searchResultsComponent = document.querySelector('mhecomm-search-v2-pagination-message');
    const useAriaLive = searchResultsComponent?.getAttribute('use-aria-live') === 'true';
    //avoid setting the focus on search page load as it can be disruptive
    if (!this.searchTriggeredOnLoad && searchResultsComponent?.shadowRoot && !useAriaLive) {
      const resultsInfoBar = searchResultsComponent.shadowRoot.getElementById('results-info-bar');
      resultsInfoBar?.focus();
    }
  }

  filterValues(): { [key: string]: Array<string> } {
    const state = store.getState() as StoreState;
    const facets: Array<SearchV2Facet> = selectFacets(state)!;
    const appliedFilter: { [key: string]: Array<string> } = {};
    facets?.forEach((facet) => {
      const values: Array<string> = facet?.facetValues
        .filter((facetValue) => facetValue.selected)
        .map((val) => val.name);
      if (values.length > 0) {
        appliedFilter[facet.name] = values;
      }
    });
    return appliedFilter;
  }

  private _searchUrl({
    searchQuery = '',
    page = '1',
    sortby = 'relevance',
    order = 'desc',
    businessUnit = 'all',
    discipline = '',
  }) {
    const url =
      this.searchResultPage == 'support'
        ? new URL(`${environment().supportSearchUrl}`)
        : new URL(`${environment().searchUrl}`);
    businessUnit = businessUnit.toLowerCase() == 'unified' ? 'all' : businessUnit;
    url.searchParams.set('searchQuery', searchQuery);
    url.searchParams.set('page', page);
    url.searchParams.set('sortby', sortby);
    url.searchParams.set('order', order);
    url.searchParams.set('bu', businessUnit.toLocaleLowerCase());

    if (discipline) {
      url.searchParams.set('disciplines', discipline);
    }

    if (this.product && this.product != 'all') {
      url.searchParams.set('product', this.product);
    }

    if (this.businessUnit && this.businessUnit != 'all') {
      url.searchParams.set('learning_level', this.businessUnit);
    }

    if (this.persona && this.persona != 'all') {
      url.searchParams.set('user_type', this.persona);
    }

    if (this.createdByDivision && this.createdByDivision != 'all') {
      url.searchParams.set('created_by_division', this.createdByDivision);
    }

    url.searchParams.set('referer', window.location.origin + window.location.pathname);
    url.searchParams.set('refererLabel', document.title.split('|')[0].trim());

    return url.toString();
  }

  private _searchForCategorySuggestion(e: Event, suggestion: TopCategoryFacetValue) {
    this.searchQuery = this.firstSuggestion();
    this.hasSelectionBeenMade = true;
    this.currentFocus = -1; // Reset focus when selecting a suggestion
    const suggestionToCatalogMap = {
      'Prek-12': 'SEG_PROGRAM',
      content: 'CONTENT',
      HigherEd: 'HE',
      Professional: 'PROFESSIONAL',
    };
    const catalog = suggestionToCatalogMap[suggestion.name];
    const redirect = this.checkSearchRedirects(catalog);
    emit({
      element: this,
      name: 'searched-for-category-suggestion',
      detail: {
        term: this.searchQuery,
        searchPageType: this.searchPageType,
        catalog: 'SUPPORT',
        redirect: redirect,
        isSearchRedirect: !!redirect,
      },
      dataLayer: true,
    });

    if (redirect && this.catalog.toUpperCase() === Catalog.unified) {
      e.preventDefault();
      window.location.href = appendQueryParamToUrl(redirect, productDiscoveryQPKey, productDiscoveryQPValue);
    } else if (this.loadResults) {
      e.preventDefault();
      if (catalog) {
        store.dispatch(setSelectedCatalog(catalog));
        store.dispatch(clearSuggestions());
        this.loadSearchResults({});
      } else {
        const params = {
          ownerId: this.getRequestOwnerId(),
          pageLength: this.pageSize,
          facets: true,
          fieldValueFilter: { DISCIPLINE_PRODUCT_TAG_PATH: [suggestion.name] },
          searchTerms: this.searchQuery,
          start: 1,
        };
        // Reset per-request analytics guards before dispatching a new search so repeat
        // searches with the same payload still emit `search-results-v2`.
        this.supportResultsAnalyticsPending = true;
        this.lastSupportResultsAnalyticsSignature = '';
        store.dispatch(performV2Search(params));
      }
    } else {
      window.location.href = this._categorySuggestionUrl(suggestion);
    }
    this._closeSuggestions(e);
  }

  private _searchForTermSuggestion(e: Event, term: string) {
    this.searchQuery = term;
    this.hasSelectionBeenMade = true;
    this.currentFocus = -1; // Reset focus when selecting a term suggestion
    const redirect = this.checkSearchRedirects(this.catalog);
    emit({
      element: this,
      name: 'searched-for-term-suggestion',
      detail: {
        term: this.searchQuery,
        url: window.location.href,
        catalog: 'SUPPORT',
        searchPageType: this.searchPageType,
      },
      dataLayer: true,
    });

    if (redirect) {
      e.preventDefault();
      window.location.href = appendQueryParamToUrl(redirect, productDiscoveryQPKey, productDiscoveryQPValue);
    } else if (this.loadResults) {
      e.preventDefault();
      this.loadSearchResults({});
    } else {
      window.location.href = this._termSuggestionUrl(term);
    }
    this._closeSuggestions(e);
  }

  private inputElement(): HTMLInputElement {
    return this.shadowRoot!.getElementById('input-search-box')! as HTMLInputElement;
  }

  /**
   * Keyboard navigation of autocomplete-catalog-menu. Triggered for keydown event on us-autocomplete-pro-catalog-input
   */
  private _catalogMenuKeyboardNav(e: KeyboardEvent) {
    const catalogMenuListItems = this.catalogMenuListItems();
    if (catalogMenuListItems.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
          this.currentFocus++;
          this.focusListElement(catalogMenuListItems);
          break;
        case 'ArrowUp':
          this.currentFocus--;
          this.focusListElement(catalogMenuListItems);
          break;
        case 'Enter':
          // Check if currentFocus is valid and an element exists at that index
          if (this.currentFocus >= 0 && this.currentFocus < catalogMenuListItems.length) {
            catalogMenuListItems[this.currentFocus].click();
            this.hasSelectionBeenMade = true;
            this.inputElement().blur();
          }
          break;
        case 'Escape':
          this.suggestionsOpen = false;
          break;
      }
    }
  }

  private catalogMenuListItems(): HTMLCollectionOf<HTMLElement> {
    return this.shadowRoot!.querySelector('#' + this.id + '-autocomplete-catalog-menu')?.getElementsByTagName(
      'li'
    )! as HTMLCollectionOf<HTMLElement>;
  }

  /**
   * Focus on autocomplete-catalog-menu li element hovered by keyboard up or down keys.
   * @param catalogMenuListItems
   */
  private focusListElement(catalogMenuListItems: HTMLCollectionOf<HTMLElement>) {
    this.unFocusListElement(catalogMenuListItems);
    if (this.currentFocus >= catalogMenuListItems.length) this.currentFocus = 0;
    if (this.currentFocus < 0) this.currentFocus = catalogMenuListItems.length - 1;
    catalogMenuListItems[this.currentFocus].classList.add('bg-light');
  }

  /**
   * Un-Focus on autocomplete-catalog-menu li element hovered by keyboard up or down keys.
   * @param catalogMenuListItems
   */
  private unFocusListElement(catalogMenuListItems: HTMLCollectionOf<HTMLElement>) {
    for (let i = 0; i < catalogMenuListItems.length; i++) {
      catalogMenuListItems[i].classList.remove('bg-light');
    }
  }

  private _categoryCount() {
    let count = 0;
    if (this.suggestions?.topCategoryFacetValues?.length != undefined) {
      count = this.suggestions?.topCategoryFacetValues.length;
    }
    return count;
  }

  private _handleOptionKeyDown(e: KeyboardEvent, callback: Function) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback();
    }
  }

  private _updateFocusedItem(index: number) {
    this.currentFocus = index;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-search-v2': SearchV2;
  }
}
